import { useState } from "react";
import { importHuggingFaceDataset } from "../../utils/fine-tuning-functions";

export default function HuggingFace() {
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [feedback, setFeedback] = useState({ type: null, message: null });

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    // Clear any previous feedback when user starts typing again
    if (feedback.message) setFeedback({ type: null, message: null });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!inputValue.trim()) {
      setFeedback({
        type: "error",
        message: "Please enter a valid HuggingFace dataset name"
      });
      return;
    }

    setIsLoading(true);
    setFeedback({ type: null, message: null });

    try {
      const result = await importHuggingFaceDataset(inputValue);

      if (!result) {
        throw new Error(`'${inputValue}' is not a valid HuggingFace dataset name. Please check the spelling and try again.`);
      }

      if (result.error || result.status === 'error') {
        throw new Error(result.error || "Failed to import dataset");
      }

      setFeedback({
        type: "success",
        message: `Successfully imported dataset: ${inputValue}`
      });
      setInputValue("");
    } catch (error) {
      console.error("Caught error:", error);
      setFeedback({
        type: "error",
        message: error.message || "Failed to import dataset. Please try again."
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Import from HuggingFace</h3>
        <p className="text-gray-600">
          Enter the name of a HuggingFace dataset to import it directly into your fine-tuning workflow.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="huggingface-dataset" className="block text-sm font-medium text-gray-700 mb-1">
            Dataset Name
          </label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
                <path d="M10.53 10.73c-.1-.31-.19-.61-.29-.92-.1-.31-.19-.61-.28-.91-.09-.3-.18-.59-.27-.87-.09-.29-.17-.56-.25-.81h-.05c-.08.25-.16.52-.24.81l-.26.87c-.09.3-.18.6-.28.91-.1.31-.19.61-.3.92h2.22zm.93 2.49h-4.1l-.94 2.87h-2.3l3.9-11.14h2.8l3.9 11.14h-2.35l-.91-2.87z" />
                <path d="M20.5 14.58h-2.1v2.15c0 .57.23.85.68.85.24 0 .44-.04.64-.12l.24 1.56c-.39.17-.93.26-1.6.26-1.83 0-2.73-.74-2.73-2.23v-2.47h-1.24v-1.69h1.24v-1.73h2.63v1.73h2.24v1.69z" />
              </svg>
            </div>
            <input
              type="text"
              id="huggingface-dataset"
              value={inputValue}
              onChange={handleInputChange}
              className="pl-10 block w-full sm:text-sm border-gray-300 rounded-md py-2 px-3 focus:ring-ktx-purple focus:border-ktx-purple"
              placeholder="e.g., Knowtex-ai/astera-dataset"
              disabled={isLoading}
            />
          </div>
          <p className="mt-1 text-xs text-gray-500">Format: dataset_name or username/dataset_name</p>
        </div>

        {feedback.message && (
          <div className={`p-3 rounded-md ${feedback.type === 'error'
            ? 'bg-red-50 text-red-700 border border-red-200'
            : 'bg-green-50 text-green-700 border border-green-200'
            }`}>
            <div className="flex">
              <div className="flex-shrink-0">
                {feedback.type === 'error' ? (
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              <div className="ml-3">
                <p className="text-sm">{feedback.message}</p>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isLoading}
            className={`bg-ktx-purple hover:bg-ktx-purple-dark text-white font-medium py-2 px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-ktx-purple focus:ring-offset-2 transition-colors
            ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
          >
            {isLoading ? (
              <div className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Importing...
              </div>
            ) : (
              'Import Dataset'
            )}
          </button>
        </div>
      </form>

    </div>
  );
}